import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import AgentStatusIndicator from '../../../components/AgentStatusIndicator';

const ConversationThread = ({ conversations, agents, onThreadSelect, selectedThreadId }) => {
  const [expandedThreads, setExpandedThreads] = useState([]);

  const toggleThread = (threadId) => {
    setExpandedThreads(prev => 
      prev?.includes(threadId) 
        ? prev?.filter(id => id !== threadId)
        : [...prev, threadId]
    );
  };

  const formatTimestamp = (date) => {
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);

    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getResponseStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return { name: 'CheckCircle2', color: 'var(--color-success)' };
      case 'processing':
        return { name: 'Loader2', color: 'var(--color-warning)' };
      case 'pending':
        return { name: 'Clock', color: 'var(--color-muted-foreground)' };
      default:
        return { name: 'Circle', color: 'var(--color-muted-foreground)' };
    }
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      urgent: { label: 'Urgent', color: 'bg-error text-error-foreground' },
      high: { label: 'High', color: 'bg-warning text-warning-foreground' },
      normal: { label: 'Normal', color: 'bg-primary text-primary-foreground' },
      low: { label: 'Low', color: 'bg-muted text-muted-foreground' }
    };
    return badges?.[priority] || badges?.normal;
  };

  if (conversations?.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <div className="w-20 h-20 mb-4 rounded-full bg-muted flex items-center justify-center">
          <Icon name="MessageSquare" size={40} color="var(--color-muted-foreground)" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">No Conversations Yet</h3>
        <p className="text-sm text-muted-foreground max-w-sm">
          Start by selecting an agent and sending your first message to begin coordinating tasks.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {conversations?.map((conversation) => {
        const agent = agents?.find(a => a?.id === conversation?.agentId);
        const isExpanded = expandedThreads?.includes(conversation?.id);
        const isSelected = selectedThreadId === conversation?.id;
        const statusIcon = getResponseStatusIcon(conversation?.responseStatus);
        const priorityBadge = getPriorityBadge(conversation?.priority);

        return (
          <div
            key={conversation?.id}
            className={`
              border-2 rounded-xl transition-all duration-250 overflow-hidden
              ${isSelected ? 'border-primary bg-primary/5' : 'border-border bg-card hover:border-primary/50'}
            `}
          >
            <div
              onClick={() => {
                toggleThread(conversation?.id);
                onThreadSelect(conversation?.id);
              }}
              className="p-4 cursor-pointer"
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="relative flex-shrink-0">
                  <Image
                    src={agent?.avatar || 'https://api.dicebear.com/7.x/shapes/svg?seed=agent'}
                    alt={agent?.avatarAlt || `Profile picture of ${agent?.name} agent with professional appearance`}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div className="absolute -bottom-1 -right-1">
                    <AgentStatusIndicator 
                      status={agent?.status || 'offline'}
                      size="sm"
                      showLabel={false}
                    />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="text-base font-semibold text-foreground truncate">
                      {agent?.name || 'Unknown Agent'}
                    </h4>
                    <span className="text-xs text-muted-foreground font-caption whitespace-nowrap ml-2">
                      {formatTimestamp(conversation?.timestamp)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${priorityBadge?.color}`}>
                      {priorityBadge?.label}
                    </span>
                    <div className="flex items-center gap-1">
                      <Icon name={statusIcon?.name} size={14} color={statusIcon?.color} />
                      <span className="text-xs text-muted-foreground capitalize">
                        {conversation?.responseStatus}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                    {conversation?.lastMessage}
                  </p>
                </div>
                <button
                  className="flex-shrink-0 p-1 rounded-lg hover:bg-muted transition-colors duration-250"
                  aria-label={isExpanded ? 'Collapse thread' : 'Expand thread'}
                >
                  <Icon 
                    name={isExpanded ? 'ChevronUp' : 'ChevronDown'} 
                    size={20} 
                    color="var(--color-muted-foreground)" 
                  />
                </button>
              </div>

              {conversation?.messageCount > 1 && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Icon name="MessageCircle" size={14} />
                  <span>{conversation?.messageCount} messages in thread</span>
                </div>
              )}
            </div>
            {isExpanded && (
              <div className="border-t border-border bg-muted/30 p-4 space-y-3">
                {conversation?.messages?.map((msg, index) => (
                  <div
                    key={index}
                    className={`
                      p-3 rounded-lg
                      ${msg?.sender === 'user' ?'bg-primary text-primary-foreground ml-8' :'bg-background mr-8'
                      }
                    `}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium font-caption">
                        {msg?.sender === 'user' ? 'You' : agent?.name}
                      </span>
                      <span className="text-xs opacity-70">
                        {formatTimestamp(msg?.timestamp)}
                      </span>
                    </div>
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">
                      {msg?.content}
                    </p>
                    {msg?.deliverables && msg?.deliverables?.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-current/20">
                        <p className="text-xs font-medium mb-2">Deliverables:</p>
                        <div className="space-y-2">
                          {msg?.deliverables?.map((deliverable, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-xs">
                              <Icon name="FileText" size={14} />
                              <span className="truncate">{deliverable}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ConversationThread;